import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CustomerVerifyLink() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setStatus('error');
      setMessage('Lien invalide - aucun token fourni');
      return;
    }

    verifyToken(token);
  }, [searchParams]);

  const verifyToken = async (token) => {
    try {
      const response = await fetch(
        `${API_URL}/api/customers/verify-magic-link?token=${token}`
      );

      const data = await response.json();

      if (response.ok && data.token && data.customer) {
        // Store access token and customer info in localStorage
        localStorage.setItem('customerToken', data.token);
        localStorage.setItem('customerData', JSON.stringify(data.customer));

        setStatus('success');
        setMessage(`Bienvenue ${data.customer.name} !`);

        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          navigate('/client/dashboard');
        }, 2000);
      } else {
        setStatus('error');
        setMessage(data.detail || 'Lien invalide ou expiré');
      }
    } catch (error) {
      console.error('Verification error:', error);
      setStatus('error');
      setMessage('Erreur de connexion au serveur');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl shadow-2xl p-8 border border-gray-700">
          {status === 'verifying' && (
            <div className="text-center">
              <Loader2 className="w-16 h-16 text-amber-500 animate-spin mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-white mb-2">Vérification en cours...</h2>
              <p className="text-gray-400">
                Nous vérifions votre lien de connexion
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500 rounded-full mb-6">
                <CheckCircle className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Connexion réussie !</h2>
              <p className="text-gray-300 mb-4">{message}</p>
              <p className="text-sm text-gray-400">
                Redirection vers votre espace client...
              </p>
              <div className="mt-6">
                <Loader2 className="w-6 h-6 text-amber-500 animate-spin mx-auto" />
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-500 rounded-full mb-6">
                <XCircle className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Erreur de connexion</h2>
              <p className="text-gray-300 mb-6">{message}</p>
              
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/client/login')}
                  className="w-full py-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-semibold rounded-lg transition-all duration-300"
                >
                  Demander un nouveau lien
                </button>
                
                <button
                  onClick={() => navigate('/')}
                  className="w-full py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-lg transition-all duration-300"
                >
                  Retour à l'accueil
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Security Note */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            🔒 Connexion sécurisée • Vos données sont protégées
          </p>
        </div>
      </div>
    </div>
  );
}
